import { getValidDateTime } from 'helpers/dateTime.helper/dateTime.helper'
import { applyToNestedString, clone, isArray, isNumber, isObject, round } from 'helpers/global.helper/global.helper'
import i18n from 'i18next'
import uiConfigStore from 'store/uiConfig/uiConfig'
import { ChartItem } from 'ui/uiConfig/components/Chart/Chart.types'
import Datetime from 'utils/datetime/datetime'
import getLogger from 'utils/log/log'
import { findClosestDivisor } from 'utils/numberUtils/numberUtils'
import { snapshot } from 'valtio'
import { isCalcDatasetInstruction } from './dataset.calc.helper'
import { type DatasetFillMethod } from './dataset.helper.types'

const log = getLogger('dataset.helper')

export const DEFAULT_DATASET_TIME_INTERVAL_MINUTES = 60
export const MIN_DATASET_TIME_INTERVAL_MINUTES = 15

export function hasSources(contract?: DatasetContract): contract is DatasetContractWithSources {
  if (!contract) {
    return false
  }
  return (<DatasetContractWithSources>contract).sources !== undefined
}

export function isDbSDatasetInstructionContract(contract: unknown): contract is DatasetContractDbS {
  return (
    !!contract &&
    typeof contract === 'object' &&
    contract !== null &&
    'metadata_filter' in contract &&
    typeof (contract as any).metadata_filter === 'object' &&
    (contract as any).metadata_filter !== null &&
    'include' in (contract as any).metadata_filter &&
    'exclude' in (contract as any).metadata_filter
  )
}

export function getDbSDatasetInstructionProperty<T>(
  contract: unknown,
  key: keyof DatasetContractDbS,
  fallback: T,
  typeGuard: (value: unknown) => value is T
): T {
  if (isDbSDatasetInstructionContract(contract) && contract[key] !== undefined) {
    const value = contract[key]
    if (typeGuard(value)) {
      return value
    }
  }
  return fallback
}

export function getEarliestStartTime(
  datasetInstructions: any[]
): `${number}-${number}-${number}T${number}:${number}:${number}+00:00` {
  return datasetInstructions.reduce(
    (t, datasetInstruction) => {
      const dt = getValidDateTime(datasetInstruction.filter?.start_time)

      dt.addHours(datasetInstruction.filter?.offset_start_time ?? 0)

      return dt.isBefore(t) ? dt.ISOString : t
    },
    Datetime.toISOString(new Date(`3000-01-01T00:00:00+00:00`))
  )
}

export function getLatestEndTime(datasetInstructions: any[]): ISODateTime {
  return datasetInstructions.reduce(
    (t, datasetInstruction) => {
      const dt = getValidDateTime(datasetInstruction.filter?.end_time)

      dt.addHours(datasetInstruction.filter?.offset_end_time ?? 0)
      return dt.isAfter(t) ? dt.ISOString : t
    },
    Datetime.toISOString(new Date(`1970-01-01T00:00:00+00:00`))
  )
}

export function aggregateDataset(
  dataset: Dataset,
  aggregate: DatasetInstructionAggregate,
  invalidValueIndeces: Set<number> = new Set()
) {
  if (!aggregate) {
    return dataset
  }

  let aggregatedValue: number | null = null
  const values = dataset.values.filter(
    (value, index) =>
      !invalidValueIndeces.has(index) && value !== null && value !== undefined && typeof value === `number`
  )

  if (!values.length) {
    return {
      ...dataset,
      values: [],
      times: [],
    }
  }

  const nonNullValues = values.filter(isNumber)

  if (aggregate === `sum`) {
    aggregatedValue = nonNullValues.reduce((acc, value) => acc + value, 0)
  } else if (aggregate === `mean`) {
    aggregatedValue = nonNullValues.reduce((acc, value) => acc + value, 0) / nonNullValues.length
  } else if (aggregate === `min`) {
    aggregatedValue = Math.min(...nonNullValues)
  } else if (aggregate === `max`) {
    aggregatedValue = Math.max(...nonNullValues)
  } else if (aggregate === `latest`) {
    aggregatedValue = nonNullValues[nonNullValues.length - 1]
  }

  if (aggregatedValue === null) {
    return {
      ...dataset,
      values: [],
      times: [],
    }
  }

  return {
    ...dataset,
    values: [aggregatedValue],
    times: [dataset.times[values.length - 1]],
  }
}

export function limitDataset<T extends Pick<Dataset, 'values'>>(dataset: T, filter: DatasetInstructionFilter): T {
  const minValue = filter.limit_min
  const maxValue = filter.limit_max

  function limitValue(value: number | null, minValue?: number, maxValue?: number): number | null {
    if (typeof value !== `number`) {
      return value
    }

    if (typeof minValue === `number` && value < minValue) {
      return minValue
    }

    if (typeof maxValue === `number` && value > maxValue) {
      return maxValue
    }

    return value
  }

  return {
    ...clone(dataset),
    values: dataset.values.map((v) => limitValue(v, minValue, maxValue)),
  }
}

export function addOneZeroValueToEndOfDataset<T extends Pick<Dataset, 'values'>>(dataset: T): T {
  const values = clone(dataset.values)
  const realValues = clone(dataset.values)

  for (let i = 0; i < values.length; i++) {
    const v = realValues[i]
    const v_prev = i > 0 ? realValues[i - 1] : null
    const v_next = i < realValues.length - 1 ? realValues[i + 1] : null

    if (v_prev && !v && !v_next) {
      values[i] = 0
    }
  }

  return {
    ...clone(dataset),
    values,
  }
}

export function setNanIfZero<T extends Pick<Dataset, 'values'>>(dataset: T, filter: DatasetInstructionFilter): T {
  if (filter?.zero_is_nan !== true) {
    return dataset
  }

  const values = clone(dataset.values)

  for (let i = 0; i < values.length; i++) {
    const v = values[i]
    const v_prev = i > 0 ? values[i - 1] : null
    const v_next = i < values.length - 1 ? values[i + 1] : null
    if (v !== 0) {
      continue
    }
    if (filter?.skip_previous_zeros && filter?.zero_is_nan && !v_prev) {
      values[i] = null
    } else if (!v_prev && !v_next) {
      values[i] = null
    }
  }

  return {
    ...clone(dataset),
    values,
  }
}

export function getDatasetsInItems(items: ChartItem[], datasets: Dataset[]): Dataset[] {
  const itemDataIds = new Set(items.map((item) => item.data_id))
  const datasetIds = new Set(datasets.map((dataset) => dataset.return_id))

  const datasetreturnIdToDatasetMap: Record<string, Dataset> = datasets.reduce((acc, dataset) => {
    return {
      ...acc,
      [dataset.return_id]: dataset,
    }
  }, {})

  const itemIdToDecimalsMap: Record<string, number> = items.reduce((acc, item) => {
    return {
      ...acc,
      [item.data_id]: item.decimals,
    }
  }, {})

  const datasetsInItems = [
    // Datasets that are in items, in correct order
    ...items
      .filter((item) => datasetIds.has(item.data_id))
      .map((item) => ({ ...datasetreturnIdToDatasetMap[item.data_id], decimal: itemIdToDecimalsMap[item.data_id] })),

    // Complement with datasets that have no items
    ...datasets.filter((dataset) => !itemDataIds.has(dataset.return_id)),
  ]

  return datasetsInItems
}

export function setSmallValuesToZero(datasets: Dataset[], limitValue: number): Dataset[] {
  datasets.forEach((dataset) => {
    dataset.values = dataset.values.map((v) => (v !== null && Math.abs(v) < limitValue ? 0 : v))
  })

  return datasets
}

export function serializeDatasetInstruction(datasetInstruction: any): any {
  let serializedContract = datasetInstruction.contract
  const type = datasetInstruction.type

  if (type === `calc`) {
    const contract = datasetInstruction.contract as DatasetContractCalc
    serializedContract = {
      variables: contract.variables || [],
      operator: contract.operator || `+`,
      element_wise: contract.element_wise ?? true,
    }
  } else if (type === `optresults`) {
    const contract = datasetInstruction.contract as DatasetContractOptResults
    serializedContract = {
      sources: contract.sources || [],
      opt_model_id: contract.opt_model_id || 0,
      opt_job_type_id: contract.opt_job_type_id ?? 2,
      subtype: contract.subtype ?? ``,
      opt_time: `$opt_time`,
    }

    if (serializedContract.subtype === ``) {
      delete serializedContract.subtype
    }
  } else if (type === 'dbs') {
    const contract = datasetInstruction.contract as DatasetContractDbS
    serializedContract = {
      metadata_filter: contract.metadata_filter || {
        include: {},
        exclude: {},
      },
      id_renaming: contract.id_renaming || {},
    }

    if (contract.global_read_api) {
      serializedContract.global_read_api = true
    }
  }

  datasetInstruction.contract = serializedContract

  return datasetInstruction
}

export function mapDatasetValuesToTimestamps(
  dataset: Pick<Dataset, 'times' | 'values'>,
  targetTimestamps: ISODateTime[],
  fillMethod: DatasetFillMethod = 'null'
): Array<number | null> {
  const datasetTimeToValueMap: Record<string, number | null> = {}

  dataset.times.forEach((time, index) => {
    datasetTimeToValueMap[time] = dataset.values[index] ?? null
  })

  const mappedValues = targetTimestamps.map((time) => datasetTimeToValueMap[time] ?? null)

  if (fillMethod !== 'forwardFill') {
    return mappedValues
  }

  let latestValue: number | null = null
  return mappedValues.map((value) => {
    if (value === null || value === undefined) {
      return latestValue
    }

    latestValue = value
    return value
  })
}

export function getShortestDatasetWithIntervalInMinutes(datasets: Dataset[]): {
  interval: number | undefined
  dataset: Dataset | undefined
} {
  return datasets.reduce(
    (smallestDatasetWithInterval: { interval: number | undefined; dataset: Dataset | undefined }, dataset) => {
      let { interval: smallestInterval, dataset: smallestDataset } = smallestDatasetWithInterval

      if (dataset.times.length < 2) {
        // Skip this dataset, keep current smallest
        return smallestDatasetWithInterval
      }

      for (let i = 1; i < dataset.times.length; i++) {
        const prevTime = dataset.times[i - 1]
        const currentTime = dataset.times[i]
        const diff = Datetime.getNrOfMinutesBetween(prevTime, currentTime)

        if (!smallestInterval || diff < smallestInterval) {
          smallestInterval = diff
          smallestDataset = dataset
        }
      }

      return { interval: smallestInterval, dataset: smallestDataset }
    },
    { interval: undefined, dataset: undefined }
  )
}

/**
 * Ensures that all datasets have uniform time intervals by filling missing timestamps with `null` values.
 *
 * - If a dataset contains only one value and `forceFill` is not set, it is returned as-is.
 * - If a dataset is empty, it is filled with all interpolated timestamps and `null` values.
 * - Otherwise, missing timestamps are filled with `null` values to match the interpolated intervals.
 *
 * @param datasets - Array of datasets to process.
 * @param startTime - The start time for interpolation.
 * @param endTime - The end time for interpolation.
 * @param forceFill - If true, fills datasets even if they contain only one value.
 * @returns Array of datasets with uniform time intervals.
 */
export function ensureUniformTimeIntervals(
  datasets: Dataset[],
  startTime: ISODateTime,
  endTime: ISODateTime,
  forceFill?: boolean
) {
  const interpolatedTimestamps: ISODateTime[] = getUniformTimestamps(datasets, startTime, endTime)

  return datasets.map((dataset) => {
    // Only one item in dataset means a aggregate-method is applied. We don't need to fill missing intervals since there
    // are no intervals.
    // In some cases we want to fill out the dataset regardless to make sure it's the same length as the other datasets.
    if (dataset.values.length === 1 && !forceFill) {
      return dataset
    }

    // Entirely empty dataset
    if (!dataset.times.length) {
      dataset.times = interpolatedTimestamps
      dataset.values = dataset.times.map(() => null)
      return dataset
    }

    const mappedValues = mapDatasetValuesToTimestamps(dataset, interpolatedTimestamps)
    dataset.times = interpolatedTimestamps
    dataset.values = mappedValues

    return dataset
  })
}

export function getReturnIdsFromDatasetInstruction(datasetInstruction: any) {
  const returnIds: string[] = []

  if (datasetInstruction.return_id?.length && datasetInstruction.return_id?.length > 0) {
    returnIds.push(datasetInstruction.return_id)
  }

  if (datasetInstruction.contract && hasSources(datasetInstruction.contract)) {
    datasetInstruction.contract?.sources?.forEach(({ return_id }: any) => {
      returnIds.push(return_id)
    })
  }

  if (datasetInstruction.type === 'dbs') {
    const contract = datasetInstruction.contract as DatasetContractDbS
    let internalIdsList = contract.metadata_filter?.include?.internal_id

    if (internalIdsList === undefined || contract.use_dbs_ids_as_return_ids) {
      internalIdsList = contract.dataset_ids?.map((id) => `${id}`) ?? []
    }

    if (typeof internalIdsList === 'string') {
      internalIdsList = [internalIdsList]
    }

    if (contract.id_renaming && isObject(contract.id_renaming)) {
      const renamedIds: string[] = []

      internalIdsList.forEach((id) => {
        if (contract.id_renaming?.[id] === undefined) {
          renamedIds.push(id)
        } else if (isArray(contract.id_renaming[id])) {
          renamedIds.push(...contract.id_renaming[id])
        } else if (typeof contract.id_renaming[id] === 'string') {
          renamedIds.push(contract.id_renaming[id])
        }
      })

      internalIdsList = renamedIds
    }

    const internalIds = new Set(internalIdsList)

    Object.entries(contract.id_renaming || {}).forEach(([internalId, returnId]) => {
      if (isArray(returnId)) {
        returnIds.push(...returnId)
      } else {
        returnIds.push(returnId)
      }

      // Skip renamed IDs
      if (internalIds.has(internalId)) {
        internalIds.delete(internalId)
      }
    })

    // Include remaining internal ids
    internalIds.forEach((internalId) => {
      returnIds.push(internalId)
    })
  }

  return [...new Set(returnIds)]
}

export function prefixAllReturnIds(_datasetInstruction: any, prefix: string | number) {
  const datasetInstruction = clone(_datasetInstruction)

  if (hasSources(datasetInstruction.contract)) {
    datasetInstruction.contract.sources =
      datasetInstruction.contract.sources?.map((source: any) => ({
        ...source,
        return_id: `${prefix}${source.return_id}`,
      })) ?? []
  }

  if (isCalcDatasetInstruction(datasetInstruction) && datasetInstruction.contract?.variables) {
    ;(datasetInstruction.contract as DatasetContractCalc).variables = (
      datasetInstruction.contract as DatasetContractCalc
    ).variables.map((variable) => `${prefix}${variable}`)
  }

  if (datasetInstruction.return_id) {
    datasetInstruction.return_id = `${prefix}${datasetInstruction.return_id}`
  }

  return datasetInstruction
}

export function getReturnIdsFromDatasetsInstruction(datasetInstructions: any[]): string[] {
  const returnIds: string[] = []

  datasetInstructions.forEach((datasetInstruction) => {
    returnIds.push(...getReturnIdsFromDatasetInstruction(datasetInstruction))
  })

  return returnIds
}

export function mergeDatasetsWithSameReturnId(datasets: any[]): any[] {
  const datasetCache = datasets.reduce(
    (acc, dataset) => {
      if (!acc[dataset.return_id]) {
        acc[dataset.return_id] = {
          status: dataset.status,
          error_msg: dataset.error_msg,
          decimal: dataset.decimal,
          timeValueMap: new Map(),
        }
      }
      const timeValueMap = acc[dataset.return_id].timeValueMap

      dataset.times.forEach((time: any, index: number) => {
        const isoDateTime = time as ISODateTime
        // Try to avoid null values when possible (add null as fallback to not introduce undefined into the map)
        timeValueMap.set(isoDateTime, dataset.values[index] ?? (timeValueMap.get(isoDateTime) || null))
      })

      return acc
    },
    {} as Record<
      string,
      Pick<Dataset, 'status' | 'error_msg' | 'decimal'> & {
        timeValueMap: Map<ISODateTime, number | null>
      }
    >
  )

  return Object.entries(datasetCache).map(([returnId, dataset]: [any, any]) => {
    const { status, error_msg, decimal, timeValueMap } = dataset
    const sortedTimeValEntries = Array.from(timeValueMap.entries()).sort(([timeA]: any, [timeB]: any) =>
      Datetime.isAfter(timeA as ISODateTime, timeB as ISODateTime) ? 1 : -1
    )
    const times = sortedTimeValEntries.map(([time]: any) => time)
    const values = sortedTimeValEntries.map(([_time, value]: any) => value)

    return {
      status,
      error_msg,
      decimal,
      return_id: returnId,
      times,
      values,
    }
  })
}

/**
 * Add new missing return_ids from DSI as null-valued datasets.
 */
export function addMissingDatasets(datasets: Dataset[], instruction: any): Dataset[] {
  const allExpectedReturnIds = Array.from(new Set(getReturnIdsFromDatasetInstruction(instruction)))
  if (!allExpectedReturnIds?.length) {
    return datasets
  }

  const allExistingIdsSet = new Set(datasets.map((dataset) => dataset.return_id))
  const additionalMissingIds = allExpectedReturnIds.filter((id) => !allExistingIdsSet.has(id))

  if (!additionalMissingIds.length) {
    return datasets
  }
  log.warn(
    `${additionalMissingIds.length} Additional missing internal_ids from DbS response. Filling datasets with null values.`,
    { additionalMissingIds, allExpectedReturnIds, allExistingIdsSet }
  )

  let allTimes: ISODateTime[] = []
  if (datasets.length > 0 && datasets[0].times?.length > 0) {
    const firstTime = datasets[0].times?.[0] as unknown as ISODateTime
    const lastTime = datasets[0]?.times?.[datasets[0]?.times?.length - 1] as unknown as ISODateTime
    allTimes = Datetime.getHoursBetween(firstTime, lastTime)
  }

  additionalMissingIds.forEach((returnId) => {
    datasets.push({
      times: allTimes,
      values: Array(allTimes.length).fill(null),
      return_id: returnId,
    })
  })

  return datasets
}

export function applyZoomToDataset(
  dataset: Dataset,
  zoomStartTime: ISODateTime | null,
  zoomEndTime: ISODateTime | null,
  ignoreZoomIds?: string[]
) {
  return applyZoomToDatasets([dataset], zoomStartTime, zoomEndTime, ignoreZoomIds)[0]
}

export function applyZoomToDatasets(
  datasets: Dataset[],
  zoomStartTime: ISODateTime | null,
  zoomEndTime: ISODateTime | null,
  ignoreZoomIds?: string[]
) {
  if (!zoomStartTime || !zoomEndTime) {
    return datasets
  }

  const ignoreZoomIdsSet = new Set(ignoreZoomIds || [])
  const zoomStartTimeDate = zoomStartTime ? new Date(zoomStartTime) : null
  const zoomEndTimeDate = zoomEndTime ? new Date(zoomEndTime) : null

  return datasets.map((dataset) => {
    if (ignoreZoomIdsSet.has(dataset.return_id)) {
      return dataset
    }

    const shouldKeepDatapoint = dataset.times.map((time) => {
      const timeDate = new Date(time)

      const zoomStartIsBeforeOrEqual = zoomStartTimeDate ? zoomStartTimeDate <= timeDate : false
      const zoomEndIsAfterOrEqual = zoomEndTimeDate ? zoomEndTimeDate >= timeDate : false
      return zoomStartIsBeforeOrEqual && zoomEndIsAfterOrEqual
    })

    return {
      ...dataset,
      values: dataset.values.filter((_value, i) => shouldKeepDatapoint[i]),
      times: dataset.times.filter((_time, i) => shouldKeepDatapoint[i]),
    }
  })
}

export type ApplyAggregateOptions = {
  skipInvalidIndices?: boolean
  invalidIndexCheckIds?: string[]
  aggregate_in_frontend?: boolean
  aggregate_function?: DatasetInstructionAggregate
}

export function datasetHasOnlyNullValues(dataset: Dataset): boolean {
  const nrOfValidIndices = dataset.values.filter((v) => v !== null).length
  return nrOfValidIndices === 0
}

/**
 * Indices of null/undefined values that lie between the first and the last real value of a
 * dataset, i.e. genuine gaps in the data. Leading and trailing nulls are excluded: those are the
 * padding `ensureUniformTimeIntervals` adds when the dataset does not cover the whole requested
 * timespan, and treating them as invalid would drop those timestamps from every other dataset's
 * aggregate as well.
 */
function getNullIndicesWithinDataRange(values: Array<number | null | undefined>): number[] {
  const firstValueIndex = values.findIndex((value) => value !== null && value !== undefined)

  if (firstValueIndex === -1) {
    return []
  }

  let lastValueIndex = values.length - 1
  while (values[lastValueIndex] === null || values[lastValueIndex] === undefined) {
    lastValueIndex--
  }

  const nullIndices: number[] = []
  for (let i = firstValueIndex; i <= lastValueIndex; i++) {
    const value = values[i]
    if (value === null || value === undefined) {
      nullIndices.push(i)
    }
  }

  return nullIndices
}

export function applyAggregateFromUiConfig(datasets: any[], uid: number, options?: ApplyAggregateOptions): any[] {
  const uiConfigSnap = snapshot(uiConfigStore)
  const uiConfig: any = uiConfigSnap.getParsedUiConfig(uid) || {
    dataset_instructions: [],
  }

  let aggregatedDataset: any[] = []

  const returnIds = datasets.map((dataset) => dataset.return_id)
  const returnIdsSet = new Set(returnIds)

  const returnIdToDatasetInstructionMap = uiConfig.dataset_instructions.reduce((acc: any, datasetInstruction: any) => {
    getReturnIdsFromDatasetInstruction(datasetInstruction).forEach((id) => {
      acc = {
        ...acc,
        [id]: datasetInstruction,
      }
    })

    return acc
  }, {})
  datasets.forEach((dataset) => {
    if (returnIdsSet.has(dataset.return_id) && !datasetHasOnlyNullValues(dataset)) {
      aggregatedDataset.push(dataset)
    }
  })
  const invalidValueIndeces: Set<number> = new Set()
  const invalidIndexCheckIds = new Set(options?.invalidIndexCheckIds || [])

  if (options?.skipInvalidIndices !== true) {
    aggregatedDataset.forEach((dataset) => {
      if (invalidIndexCheckIds.size && !invalidIndexCheckIds.has(dataset.return_id)) {
        return
      }

      // Only gaps *inside* a dataset's own data range make an index invalid. Leading/trailing
      // nulls are padding added by `ensureUniformTimeIntervals` when the requested timespan is
      // wider than the data, and must not shrink the aggregate of the other datasets.
      getNullIndicesWithinDataRange(dataset.values).forEach((i) => invalidValueIndeces.add(i))
    })
  }
  aggregatedDataset = aggregatedDataset
    .map((dataset) => {
      const aggregateInFrontend =
        options?.aggregate_in_frontend ??
        returnIdToDatasetInstructionMap[dataset.return_id]?.filter?.aggregate_in_frontend
      const aggregateFunction =
        options?.aggregate_function ?? returnIdToDatasetInstructionMap[dataset.return_id]?.filter?.aggregate
      if (aggregateInFrontend && aggregateFunction) {
        return aggregateDataset(clone(dataset), aggregateFunction, invalidValueIndeces)
      }

      return dataset
    })
    .filter((d) => !!d)
    .map((dataset) => limitDataset(dataset, returnIdToDatasetInstructionMap[dataset.return_id]?.filter || {}))
    .map((dataset) => setNanIfZero(dataset, returnIdToDatasetInstructionMap[dataset.return_id]?.filter || {}))

  return aggregatedDataset
}

export function applyAliasToDatasetInstruction(
  datasetInstruction: DatasetInstruction,
  alias: UiConfigAliases
): DatasetInstruction {
  if (!Object.keys(alias).length) {
    return datasetInstruction
  }

  return applyToNestedString<DatasetInstruction>(datasetInstruction, (str: string): string | number => {
    if (str.startsWith(`$`)) {
      const key = str.substring(1, str.length)
      const aliasValue = alias[key]

      if (Array.isArray(aliasValue)) {
        return str
      }

      return aliasValue ?? str
    }

    return str
  })
}

export function fillPeriodWithFirstIndexValue(datasetInstruction: DatasetInstruction, dataset: Dataset): Dataset {
  const earliestStartTime = getEarliestStartTime([datasetInstruction])
  const latestEndTime = getLatestEndTime([datasetInstruction])
  const interval = getShortestDatasetWithIntervalInMinutes([dataset]).interval || DEFAULT_DATASET_TIME_INTERVAL_MINUTES
  dataset.times = Datetime.getDateTimesBetween(earliestStartTime, latestEndTime, interval)
  dataset.values = dataset.times.map(() => dataset.values[0] ?? null)

  return dataset
}

export function serializeTableAccessor(accessor: string): string {
  return accessor.replace(/[^a-zA-Z0-9_]/g, `_`)
}

export function normalizeDatasetId(id: string): string {
  return id.replace(/[-._\s]/g, '').toLowerCase()
}

export function getAggregatedDatasets(datasets: any[], aggregate: 'Sum' | 'Average' | 'Max' | 'Min') {
  const aggregateRecord: Record<string, unknown>[] = [{ time: i18n.t(aggregate) }]

  if (!datasets.find((datasetColumn) => datasetColumn.times?.includes(i18n.t(aggregate)))) {
    datasets.forEach((datasetColumn) => {
      aggregateRecord[0][serializeTableAccessor(datasetColumn.return_id)] = aggregatedDataset(
        datasetColumn,
        aggregate
      ).value
    })
  }
  return aggregateRecord
}

function aggregatedDataset(dataset: any, aggregate: 'Sum' | 'Average' | 'Max' | 'Min') {
  const nonNullValues = dataset.values.filter((value: any): value is number => value !== null && value !== '-')

  let value: any = 0
  if (aggregate === 'Sum' || aggregate === 'Average') {
    value = nonNullValues.reduce((sum: any, value: any) => sum + value, 0)
    value = nonNullValues.length > 0 ? (aggregate === 'Average' ? value / nonNullValues.length : value) : '-'
  } else if (aggregate === 'Max') {
    value = nonNullValues.length > 0 ? Math.max(...nonNullValues) : '-'
  } else if (aggregate === 'Min') {
    value = nonNullValues.length > 0 ? Math.min(...nonNullValues) : '-'
  }

  return { returnId: dataset.return_id, value: isNumber(value) ? round(value, dataset.decimal ?? 2) : `-` }
}

/**
 * Generates an array of interpolated ISO date-time timestamps with uniform interval between the given
 * start and end times, based on the smallest time resolution found in the provided datasets.
 *
 * The function calculates hourly timestamps between `startTime` and `endTime`, then subdivides each hour
 * into intervals determined by the closest divisor of 60 to the smallest dataset interval (in minutes).
 * If no interval is found in the datasets, a default value is used.
 *
 * @param datasets - An array of datasets used to determine the smallest time resolution.
 * @param startTime - The ISO date-time string representing the start of the interval.
 * @param endTime - The ISO date-time string representing the end of the interval.
 * @returns An array of ISO date-time strings representing the interpolated timestamps.
 */
function getUniformTimestamps(datasets: Dataset[], startTime: ISODateTime, endTime: ISODateTime): ISODateTime[] {
  const hourTimestamps = Datetime.getHoursBetween(startTime, endTime)

  const smallestTimeResolutionInDatasets =
    getShortestDatasetWithIntervalInMinutes(datasets).interval || DEFAULT_DATASET_TIME_INTERVAL_MINUTES
  const clampedInterval = Math.max(smallestTimeResolutionInDatasets, MIN_DATASET_TIME_INTERVAL_MINUTES)
  const snappedInterval = findClosestDivisor(clampedInterval, 60)
  const numberOfIntervalsPerHour = 60 / snappedInterval

  const interpolatedTimestamps: ISODateTime[] = []
  hourTimestamps.forEach((hour) => {
    for (let i = 0; i < numberOfIntervalsPerHour; i++) {
      interpolatedTimestamps.push(Datetime.addMinutes(hour, i * snappedInterval))
    }
  })
  return interpolatedTimestamps
}
